import { type JSX } from 'react'
import { Input } from '@ttab/elephant-ui'

/**
 * The four text fields a memo cannot be created without. Planning marks a
 * required field that is still empty by tinting its placeholder and its border
 * (`components/Form/Group.tsx`), so this dialog says it the same way -
 * `placeholder-shown` keeps it to the empty state, leaving a value the editor
 * typed as ordinary text on an ordinary border.
 *
 * Each field is only as wide as what is in it, and as wide as its placeholder
 * while it is empty, so that a title and its slugline share a row without
 * either claiming space it has nothing to put in. That is `field-sizing`, which
 * needs the width left to it - an `Input` is `w-full` otherwise - and a cap, so
 * that a long value wraps onto a row of its own instead of overflowing.
 */
const REQUIRED_FIELD_CLASS = `h-7 pt-2 text-medium
  field-sizing-content w-auto max-w-full
  placeholder:text-[#5D709F] placeholder-shown:border-[#5D709F]
  read-only:text-muted-foreground`

/**
 * One of the memo's required text fields.
 *
 * The callback is deliberately not named `onChange`: `Form.Group` clones its
 * children with the form's own props (`components/Form/lib/cloneChildren.ts`),
 * and an injected `onChange` would take this one's place. The call sites keep
 * the row `div` that shields them from that, and the name means a dropped
 * wrapper cannot silently stop the field from taking input either.
 */
export const MemoTextField = ({
  label,
  placeholder,
  value,
  onValueChange,
  autoFocus,
  readOnly
}: {
  /** Names the field to a screen reader; there is no visible label. */
  label: string
  placeholder: string
  value: string
  onValueChange: (value: string) => void
  autoFocus?: boolean
  readOnly?: boolean
}): JSX.Element => (
  <Input
    autoFocus={autoFocus}
    readOnly={readOnly}
    className={REQUIRED_FIELD_CLASS}
    aria-label={label}
    placeholder={placeholder}
    value={value}
    onChange={(event) => onValueChange(event.target.value)}
  />
)
